import { useState, useEffect } from 'react'
import { useSubmitReportMutation, useGetReportReasonsQuery } from '../../store/api/moderationApi'
import { useLoginPrompt } from '../../context/LoginPromptContext'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { REPORT_REASONS, COLOR_ERROR, COLOR_ERROR_BG, COLOR_ERROR_BORDER } from '../../lib/config'

interface ReportModalProps {
  targetType: 'discussion' | 'comment'
  targetId:   string
  onClose:    () => void
}

export default function ReportModal({ targetType, targetId, onClose }: ReportModalProps) {
  const { isAuthenticated } = useAppSelector((s) => s.auth)
  const { trigger: promptLogin } = useLoginPrompt()
  const [submitReport, { isLoading }] = useSubmitReportMutation()

  // Fetch reasons from backend — fall back to hardcoded list if the request fails
  const { data: reasonsData, isLoading: reasonsLoading } = useGetReportReasonsQuery()
  const reasons: string[] = reasonsData?.reasons ?? [...REPORT_REASONS]

  const [reason, setReason]           = useState('')
  const [customReason, setCustomReason] = useState('')
  const [done, setDone]               = useState(false)
  const [error, setError]             = useState('')

  // Gate — must be logged in
  useEffect(() => {
    if (!isAuthenticated) {
      onClose()
      promptLogin('Sign in to report content')
    }
  }, [isAuthenticated])

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onClose])

  // Reset selection if reasons reload and current selection no longer exists
  useEffect(() => {
    if (reason && reasons.length > 0 && !reasons.includes(reason)) {
      setReason('')
    }
  }, [reasons])

  const handleSubmit = async () => {
    if (!reason) { setError('Please select a reason'); return }
    if (reason === 'Other' && !customReason.trim()) { setError('Please describe your reason'); return }
    setError('')
    const finalReason = reason === 'Other' ? `Other: ${customReason.trim()}` : reason
    try {
      await submitReport({ targetType, targetId, reason: finalReason }).unwrap()
      setDone(true)
    } catch {
      setError('Failed to submit report. Please try again.')
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        animation: 'rmFade 0.15s ease',
      }}
    >
      <style>{`
        @keyframes rmFade  { from { opacity:0 } to { opacity:1 } }
        @keyframes rmSlide { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
        @keyframes rmShimmer { 0% { background-position:200% 0 } 100% { background-position:-200% 0 } }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '1.5rem',
          width: '100%', maxWidth: '400px',
          boxShadow: 'var(--shadow-elevated)',
          position: 'relative',
          animation: 'rmSlide 0.18s ease',
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: '50%', width: '28px', height: '28px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.8rem',
          }}
        >✕</button>

        {done ? (
          /* ── Success state ── */
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✅</div>
            <h3 style={{ margin: '0 0 0.5rem', color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 700 }}>
              Report submitted
            </h3>
            <p style={{ margin: '0 0 1.25rem', color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.5 }}>
              Thanks for helping keep Ekkam AI safe. Our team will review this shortly.
            </p>
            <button
              onClick={onClose}
              style={{
                padding: '0.6rem 1.5rem',
                background: 'var(--accent-primary)', color: 'white',
                border: 'none', borderRadius: '8px',
                fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        ) : (
          /* ── Form state ── */
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', paddingRight: '2rem' }}>
              <span style={{ fontSize: '1.25rem' }}>🚩</span>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Report {targetType}
              </h3>
            </div>

            <p style={{ margin: '0 0 1rem', fontSize: '0.825rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Why are you reporting this content?
            </p>

            {/* Reason options — skeleton while loading */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1.25rem' }}>
              {reasonsLoading
                ? [1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      style={{
                        height: '40px', borderRadius: '8px',
                        background: 'var(--bg-elevated)',
                        backgroundImage: 'linear-gradient(90deg, var(--bg-elevated) 25%, var(--bg-sidebar) 50%, var(--bg-elevated) 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'rmShimmer 1.4s infinite',
                        opacity: 1 - i * 0.1,
                      }}
                    />
                  ))
                : reasons.map((r) => (
                    <label
                      key={r}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.65rem',
                        padding: '0.6rem 0.75rem',
                        background: reason === r ? COLOR_ERROR_BG : 'var(--bg-elevated)',
                        border: `1px solid ${reason === r ? 'rgba(239,68,68,0.4)' : 'var(--border)'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.1s',
                      }}
                    >
                      <input
                        type="radio"
                        name="report-reason"
                        value={r}
                        checked={reason === r}
                        onChange={() => { setReason(r); setError(''); if (r !== 'Other') setCustomReason('') }}
                        style={{ accentColor: COLOR_ERROR, width: '15px', height: '15px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{r}</span>
                    </label>
                  ))
              }
            </div>

            {reason === 'Other' && (
              <textarea
                value={customReason}
                onChange={(e) => { setCustomReason(e.target.value); setError('') }}
                placeholder="Please describe the issue..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem',
                  marginBottom: '0.75rem',
                  background: 'var(--bg-elevated)',
                  border: `1px solid var(--border)`,
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem',
                  resize: 'none',
                  outline: 'none',
                  boxSizing: 'border-box',
                  lineHeight: 1.5,
                  fontFamily: 'inherit',
                }}
              />
            )}

            {error && (
              <div style={{
                background: COLOR_ERROR_BG,
                border: `1px solid ${COLOR_ERROR_BORDER}`,
                borderRadius: '8px',
                padding: '0.6rem 0.75rem',
                color: COLOR_ERROR,
                fontSize: '0.8rem',
                marginBottom: '1rem',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '0.6rem 1.1rem',
                  background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: '8px', color: 'var(--text-muted)',
                  cursor: 'pointer', fontSize: '0.875rem',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => void handleSubmit()}
                disabled={isLoading || !reason || reasonsLoading || (reason === 'Other' && !customReason.trim())}
                style={{
                  padding: '0.6rem 1.25rem',
                  background: isLoading || !reason || reasonsLoading || (reason === 'Other' && !customReason.trim()) ? 'var(--bg-elevated)' : COLOR_ERROR,
                  color: isLoading || !reason || reasonsLoading || (reason === 'Other' && !customReason.trim()) ? 'var(--text-muted)' : 'white',
                  border: 'none', borderRadius: '8px',
                  fontWeight: 600, fontSize: '0.875rem',
                  cursor: isLoading || !reason || reasonsLoading || (reason === 'Other' && !customReason.trim()) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {isLoading ? 'Submitting...' : 'Submit report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
